import { Controller } from "@hotwired/stimulus"

// Handles currency-aware amount input validation
// Restricts decimal places based on the currency's exponent (e.g., 2 for EUR, 0 for JPY, 3 for KWD)
export default class extends Controller {
  static targets = ["amount", "minBookingValue", "currency"]
  static values = {
    exponents: Object // Map of currency code to exponent, e.g., { "eur": 2, "jpy": 0, "kwd": 3 }
  }

  connect() {
    this.convertInitialValues()
    this.updateAllFields()
  }

  // The backend stores amounts as integers (cents), but the UI displays them as floats (major units).
  // When a validation error occurs, the backend re-renders the form with the raw integer value (e.g., 7799).
  // We detect these integer-only values on connect and convert them back to the correct decimal format (e.g., 77.99)
  // based on the currency's exponent.
  convertInitialValues() {
    const exponent = this.getCurrentExponent()

    const convert = (field) => {
      if (/^\d+$/.test(field.value) && field.value.length > 0) {
        if (!field.value.includes('.') && !field.value.includes(',')) {
          const intValue = parseInt(field.value, 10)
          const divisor = Math.pow(10, exponent)

          if (divisor > 0) {
            const majorValue = intValue / divisor
            field.value = majorValue.toFixed(exponent)
          }
        }
      }
    }

    if (this.hasAmountTarget) convert(this.amountTarget)
    if (this.hasMinBookingValueTarget) convert(this.minBookingValueTarget)
  }

  disconnect() { }

  onCurrencyChange() {
    this.updateAllFields()
  }

  onInput(event) {
    this.validateFieldValue(event.target, this.getCurrentExponent())
  }

  onKeydown(event) {
    this.handleKeydown(event, this.getCurrentExponent())
  }

  onBlur(event) {
    const field = event.target
    if (field.value === '') return

    const exponent = this.getCurrentExponent()
    let value = field.value.replace(',', '.')
    value = value.replace(/[^\d.]/g, '')

    const parsed = parseFloat(value)
    if (!isNaN(parsed)) {
      field.value = parsed.toFixed(exponent)
    }
  }

  updateAllFields() {
    if (this.hasAmountTarget) {
      this.setupField(this.amountTarget)
    }
    if (this.hasMinBookingValueTarget) {
      this.setupField(this.minBookingValueTarget)
    }
  }

  setupField(field) {
    const exponent = this.getCurrentExponent()

    // Update placeholder based on exponent
    if (exponent === 0) {
      field.setAttribute('placeholder', 'e.g., 100')
      field.setAttribute('inputmode', 'numeric')
    } else {
      field.setAttribute('placeholder', `e.g., 100.${'0'.repeat(exponent)}`)
      field.setAttribute('inputmode', 'decimal')
    }

    // Validate current value
    this.validateFieldValue(field, exponent)
  }

  getCurrentExponent() {
    const select = this.hasCurrencyTarget ? this.currencyTarget : this.element.querySelector('select[name*="currency"]')
    if (!select) return 2 // Default to 2 decimals

    const currency = select.value.toLowerCase()
    return this.exponentsValue[currency] ?? 2
  }

  handleKeydown(event, exponent) {
    // Allow control keys
    if (['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return
    }

    // Allow select all, copy, paste, cut
    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
      return
    }

    // Allow digits
    if (/^\d$/.test(event.key)) {
      return
    }

    // For zero-decimal currencies, block decimal separators entirely
    if (exponent === 0) {
      if (event.key === '.' || event.key === ',') {
        event.preventDefault()
        return
      }
    }

    // Allow decimal separator (. or ,) for non-zero exponent currencies
    if (exponent > 0 && (event.key === '.' || event.key === ',')) {
      const value = event.target.value
      // Only allow one decimal separator
      if (value.includes('.') || value.includes(',')) {
        event.preventDefault()
      }
      return
    }

    // Block everything else
    event.preventDefault()
  }


  validateFieldValue(field, exponent) {
    let value = field.value

    // Replace comma with dot for consistency
    value = value.replace(',', '.')

    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^\d.]/g, '')

    // Ensure only one decimal point
    const parts = value.split('.')
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('')
    }

    // For zero-decimal currencies, remove any decimal part
    if (exponent === 0) {
      value = value.split('.')[0]
    } else if (parts.length === 2 && parts[1].length > exponent) {
      // Limit decimal places to currency exponent
      value = parts[0] + '.' + parts[1].substring(0, exponent)
    }

    // Only update if value changed to avoid cursor jumping
    if (field.value !== value) {
      const cursorPos = field.selectionStart
      const oldLength = field.value.length
      field.value = value
      const newLength = value.length
      // Adjust cursor position
      const newCursorPos = Math.max(0, cursorPos - (oldLength - newLength))
      field.setSelectionRange(newCursorPos, newCursorPos)
    }
  }
}
